<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { practiceMcqs, practiceUnits } from "~/features/computer/data/practice/practice.data";

type PracticeMode = "random" | "sequential" | "daily";

const selectedUnit = ref<(typeof practiceUnits)[number]["value"]>("all");
const selectedDifficulty = ref<"all" | "easy" | "medium" | "hard">("all");
const mode = ref<PracticeMode>("random");
const sessionStarted = ref(false);
const sessionFinished = ref(false);
const currentIndex = ref(0);
const score = ref(0);
const selectedOption = ref<number | null>(null);
const answers = ref<number[]>([]);
const weakTopics = ref<string[]>([]);
const practiceWeakOnly = ref(false);
const dailyScore = ref<number | null>(null);

const faqs = [
  {
    question: "11th class computer MCQs practice ka best use kya hai?",
    answer:
      "MCQs practice random test aur weak-topic revision ke liye best hoti hai. Is se student ko objective paper ki speed aur recall dono improve karne me madad milti hai.",
  },
  {
    question: "Practice MCQs aur Top 100 MCQs me kya difference hai?",
    answer:
      "Practice MCQs test mode aur scoring ke liye hain, jab ke Top 100 MCQs fast shortlist revision ke liye better hote hain.",
  },
  {
    question: "Kya practice MCQs sab chapters ko cover karte hain?",
    answer:
      "Haan, practice MCQs mixed chapter filters ke sath aate hain taake student all-chapter objective prep kar sake.",
  },
];

function shuffle<T>(items: T[]) {
  const copy = [...items];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }

  return copy;
}

function getTodaySeed() {
  return new Date().toDateString();
}

function seededShuffle<T>(items: T[], seed: string) {
  let hash = seed.split("").reduce((total, char) => total + char.charCodeAt(0), 0);

  return [...items].sort(() => {
    hash = (hash * 9301 + 49297) % 233280;
    return hash / 233280 - 0.5;
  });
}

const filteredQuestions = computed(() => {
  const base = practiceMcqs.filter((question) => (
    (selectedUnit.value === "all" || question.unit === selectedUnit.value)
    && (selectedDifficulty.value === "all" || question.difficulty === selectedDifficulty.value)
  ));

  if (!practiceWeakOnly.value || weakTopics.value.length === 0) {
    return base;
  }

  return base.filter((question) => weakTopics.value.includes(question.topic));
});

const sessionQuestions = computed(() => {
  if (mode.value === "daily") {
    return seededShuffle(filteredQuestions.value, getTodaySeed()).slice(0, 10);
  }

  if (mode.value === "random") {
    return shuffle(filteredQuestions.value).slice(0, 10);
  }

  return filteredQuestions.value.slice(0, 10);
});

const currentQuestion = computed(() => sessionQuestions.value[currentIndex.value] ?? null);
const progressLabel = computed(() => `Question ${Math.min(currentIndex.value + 1, sessionQuestions.value.length)} / ${sessionQuestions.value.length || 0}`);
const canStart = computed(() => sessionQuestions.value.length > 0);
const isLastQuestion = computed(() => currentIndex.value >= sessionQuestions.value.length - 1);

function resetSessionState() {
  sessionStarted.value = false;
  sessionFinished.value = false;
  currentIndex.value = 0;
  score.value = 0;
  selectedOption.value = null;
  answers.value = [];
}

function startPractice() {
  resetSessionState();
  sessionStarted.value = true;
}

function selectAnswer(optionIndex: number) {
  const question = currentQuestion.value;

  if (!question || selectedOption.value !== null) {
    return;
  }

  selectedOption.value = optionIndex;
  answers.value.push(optionIndex);

  if (optionIndex === question.correctIndex) {
    score.value += 1;
    return;
  }

  if (!weakTopics.value.includes(question.topic)) {
    weakTopics.value.push(question.topic);
  }
}

function nextQuestion() {
  if (selectedOption.value === null) {
    return;
  }

  if (isLastQuestion.value) {
    sessionFinished.value = true;
    sessionStarted.value = false;

    if (mode.value === "daily" && import.meta.client) {
      localStorage.setItem("dailyPracticeScore", String(score.value));
      localStorage.setItem("dailyPracticeDate", getTodaySeed());
      dailyScore.value = score.value;
    }

    return;
  }

  currentIndex.value += 1;
  selectedOption.value = null;
}

function retry() {
  practiceWeakOnly.value = false;
  startPractice();
}

function practiceWeakAreas() {
  if (weakTopics.value.length === 0) {
    return;
  }

  practiceWeakOnly.value = true;
  startPractice();
}

onMounted(() => {
  if (!import.meta.client) {
    return;
  }

  const savedDate = localStorage.getItem("dailyPracticeDate");
  const savedScore = localStorage.getItem("dailyPracticeScore");

  if (savedDate === getTodaySeed() && savedScore !== null) {
    dailyScore.value = Number(savedScore);
  }
});

watch([selectedUnit, selectedDifficulty, mode], () => {
  practiceWeakOnly.value = false;
  resetSessionState();
});

usePageSeo({
  title: "Computer MCQs Practice | 11th Class Computer",
  description: "Online MCQ test for 11th class computer with random mode, daily 10, scoring and weak-area practice.",
  keywords: "11th class computer mcqs practice, first year computer online mcq test, computer daily mcqs practice",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Practice Mode", item: "/11th/computer/practice/" },
    { name: "MCQs", item: "/11th/computer/practice/mcqs/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Practice MCQs",
    items: [
      { name: "MCQs Hub", path: "/11th/computer/mcqs/" },
      { name: "Top 100 MCQs", path: "/11th/computer/mcqs/top-100/" },
      { name: "Practice Short Questions", path: "/11th/computer/practice/short-questions/" },
      { name: "Practice Long Questions", path: "/11th/computer/practice/long-questions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="space-y-4 rounded-3xl bg-slate-950 p-6 text-white">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">Rozana Practice</p>
        <h1 class="text-2xl font-bold tracking-tight">Practice MCQs (All Chapters)</h1>
        <p class="text-sm leading-7 text-slate-200">Random mode, scoring, daily 10 aur weak-area retry ke sath mixed syllabus practice karo.</p>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5">
        <h2 class="text-lg font-semibold text-slate-900">Mode</h2>
        <div class="mt-4 grid grid-cols-3 gap-3">
          <button type="button" class="rounded-2xl border px-3 py-3 text-sm font-medium transition" :class="mode === 'random' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="mode = 'random'">
            Random
          </button>
          <button type="button" class="rounded-2xl border px-3 py-3 text-sm font-medium transition" :class="mode === 'sequential' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="mode = 'sequential'">
            Sequential
          </button>
          <button type="button" class="rounded-2xl border px-3 py-3 text-sm font-medium transition" :class="mode === 'daily' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="mode = 'daily'">
            Daily 10
          </button>
        </div>

        <h2 class="mt-5 text-lg font-semibold text-slate-900">Filters</h2>
        <div class="mt-4 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none]">
          <button
            v-for="unit in practiceUnits"
            :key="unit.value"
            type="button"
            class="snap-start shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition"
            :class="selectedUnit === unit.value ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'"
            @click="selectedUnit = unit.value"
          >
            {{ unit.value === 'all' ? 'All Units' : unit.label }}
          </button>
        </div>

        <h2 class="mt-5 text-lg font-semibold text-slate-900">Difficulty</h2>
        <div class="mt-4 flex flex-wrap gap-2">
          <button type="button" class="rounded-full border px-3 py-1 text-xs font-medium transition" :class="selectedDifficulty === 'all' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="selectedDifficulty = 'all'">All</button>
          <button type="button" class="rounded-full border px-3 py-1 text-xs font-medium transition" :class="selectedDifficulty === 'easy' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="selectedDifficulty = 'easy'">Easy</button>
          <button type="button" class="rounded-full border px-3 py-1 text-xs font-medium transition" :class="selectedDifficulty === 'medium' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="selectedDifficulty = 'medium'">Medium</button>
          <button type="button" class="rounded-full border px-3 py-1 text-xs font-medium transition" :class="selectedDifficulty === 'hard' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="selectedDifficulty = 'hard'">Hard</button>
        </div>

        <div class="mt-5 grid gap-3 sm:grid-cols-2">
          <div class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Session size</p>
            <p class="mt-1 text-2xl font-bold text-slate-900">{{ sessionQuestions.length }}</p>
            <p class="mt-1 text-sm text-slate-600">Questions ready</p>
          </div>
          <div v-if="mode === 'daily'" class="rounded-2xl bg-sky-50 p-4 ring-1 ring-sky-200">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Today's Score</p>
            <p class="mt-1 text-2xl font-bold text-slate-900">{{ dailyScore ?? "-" }}</p>
            <p class="mt-1 text-sm text-slate-600">Saved daily result</p>
          </div>
        </div>

        <button
          type="button"
          class="mt-5 w-full rounded-2xl px-4 py-3 text-sm font-semibold transition"
          :class="canStart ? 'bg-slate-950 text-white hover:bg-slate-900' : 'cursor-not-allowed bg-slate-200 text-slate-500'"
          :disabled="!canStart"
          @click="startPractice"
        >
          {{ mode === 'daily' ? 'Start Today' : 'Start Practice' }}
        </button>
      </section>

      <section v-if="!sessionStarted && !sessionFinished" class="rounded-3xl border border-amber-200 bg-amber-50 p-5">
        <h2 class="text-lg font-semibold text-slate-900">Ready to start</h2>
        <p class="mt-2 text-sm leading-7 text-slate-700">Practice start karne se pehle mode aur filters choose karo. MCQs start button ke baad one-by-one show honge.</p>
      </section>

      <section v-if="sessionStarted && currentQuestion" class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <div class="flex items-center justify-between gap-4">
          <p class="text-sm font-semibold text-slate-900">{{ progressLabel }}</p>
          <div class="flex gap-2 text-xs font-medium">
            <span class="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{{ currentQuestion.unitLabel }}</span>
            <span class="rounded-full bg-sky-100 px-3 py-1 text-sky-800">{{ currentQuestion.difficulty }}</span>
          </div>
        </div>

        <div class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <p class="text-base font-semibold text-slate-900">{{ currentQuestion.question }}</p>
        </div>

        <div class="space-y-2">
          <button
            v-for="(option, optionIndex) in currentQuestion.options"
            :key="`${currentQuestion.id}-${optionIndex}`"
            type="button"
            class="w-full rounded-2xl border px-4 py-3 text-left text-sm transition"
            :class="selectedOption === null
              ? 'border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50'
              : optionIndex === currentQuestion.correctIndex
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                : selectedOption === optionIndex
                  ? 'border-rose-300 bg-rose-50 text-rose-800'
                  : 'border-slate-200 bg-white text-slate-500'"
            :disabled="selectedOption !== null"
            @click="selectAnswer(optionIndex)"
          >
            {{ option }}
          </button>
        </div>

        <div v-if="selectedOption !== null" class="space-y-4 rounded-2xl bg-slate-50 p-4">
          <p class="text-sm font-semibold" :class="selectedOption === currentQuestion.correctIndex ? 'text-emerald-700' : 'text-rose-700'">
            {{ selectedOption === currentQuestion.correctIndex ? "Correct" : "Incorrect" }}
          </p>
          <p class="text-sm leading-7 text-slate-700">{{ currentQuestion.explanation }}</p>
          <div class="flex flex-wrap gap-3 text-sm">
            <NuxtLink :to="currentQuestion.conceptTo" class="font-medium text-sky-700 underline decoration-sky-300 underline-offset-4">Yeh topic dobara parho -></NuxtLink>
            <NuxtLink :to="currentQuestion.chapterTo" class="font-medium text-slate-700 underline decoration-slate-300 underline-offset-4">Go to chapter -></NuxtLink>
          </div>
          <button type="button" class="w-full rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white" @click="nextQuestion">
            {{ isLastQuestion ? "Finish Practice" : "Next Question" }}
          </button>
        </div>
      </section>

      <section v-if="sessionFinished" class="space-y-4 rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Final Screen</p>
        <h2 class="text-2xl font-bold tracking-tight text-slate-900">Score: {{ score }} / {{ sessionQuestions.length }}</h2>
        <p class="text-sm leading-7 text-slate-700">
          {{ score >= Math.ceil(sessionQuestions.length * 0.7)
            ? "Core revision theek ja rahi hai. Ab weak topics bhi check kar lo."
            : "Abhi weak areas par focused retry zyada useful hogi." }}
        </p>

        <div class="grid gap-3">
          <button type="button" class="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white" @click="retry">
            Retry
          </button>
          <button
            type="button"
            class="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-900"
            :class="weakTopics.length === 0 ? 'cursor-not-allowed opacity-50' : ''"
            :disabled="weakTopics.length === 0"
            @click="practiceWeakAreas"
          >
            Practice Weak Areas
          </button>
        </div>

        <div class="rounded-2xl bg-white p-4 ring-1 ring-emerald-100">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Weak Topics</p>
          <p class="mt-2 text-sm text-slate-700">
            {{ weakTopics.length ? weakTopics.join(", ") : "No weak topic captured in this round." }}
          </p>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Practice MCQs FAQs</h2>
        </div>
        <div class="mt-5 space-y-4">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>

